import { xss } from '@hc-frontend/core-utils-format';
import { DOMAIN_PARTS } from '@hc-frontend/core-utils-validations';

/**
 * Extract query parameters from a url in a safe manner, preventing xss attacks
 * @public
 *
 * @param url - An url to extract the parameters
 *
 * @param keyToLowerCase - url parameter will be lower cased
 *
 * @returns The url safe parameters as a key-value dictionary.
 *
 * @example
 * ```ts
 * let parameters = getSafeURLParameters('?p.fname=fname&p.lname=lname')
 * console.log(parameters);
 * // -> {fname: 'fname',lname:'lname}
 * ```
 */
function getSafeURLParameters(
  url: string,
  keyToLowerCase = false,
): Record<string, string> {
  const result: Record<string, string> = {};
  const stripURL = url.trim().split('#')[0];

  if (stripURL === '') {
    return result;
  }

  const parts = stripURL.match(DOMAIN_PARTS);

  const queryString = parts?.[6];
  const searchParams = new URLSearchParams(queryString);

  searchParams.forEach((value, key) => {
    const urlKey = keyToLowerCase ? key.toLowerCase() : key;
    result[urlKey.replace('p.', '')] = xss(decodeURIComponent(value));
  });

  return result;
}

export { getSafeURLParameters };
